import React, { useState, useEffect } from 'react';
import { Save, RefreshCw, AlertTriangle, CheckCircle, Settings } from 'lucide-react';
import { superAdminService } from '../../../services/superAdminService';

const SystemSettings = () => {
  const [settings, setSettings] = useState({
    systemName: 'SaaS Platform',
    systemDescription: 'Multi-tenant SaaS application',
    maintenanceMode: false,
    allowNewRegistrations: true,
    defaultUserRole: 'org_user',
    maxOrganizationsPerUser: 3,
    sessionTimeout: 30,
    passwordMinLength: 8,
    requirePasswordSpecialChars: true,
    emailNotifications: true,
    auditLogRetention: 90,
    backupFrequency: 'daily'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    fetchSystemSettings();
  }, []);

  const fetchSystemSettings = async () => {
    try {
      setLoading(true);
      const data = await superAdminService?.getSystemSettings();
      if (data) {
        setSettings({ ...settings, ...data });
      }
      setError(null);
    } catch (error) {
      setError('Failed to load system settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setSuccess(false);
      await superAdminService?.updateSystemSettings(settings);
      setSuccess(true);
      setError(null);
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      setError('Failed to save system settings');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-4">
            {[...Array(8)]?.map((_, i) => (
              <div key={i} className="h-16 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">System Settings</h2>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchSystemSettings}
            className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 flex items-center"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center disabled:opacity-50"
          >
            {saving ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </>
            )}
          </button>
        </div>
      </div>
      {/* Status Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
          <div className="flex">
            <AlertTriangle className="w-5 h-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-6">
          <div className="flex">
            <CheckCircle className="w-5 h-5 text-green-400" />
            <div className="ml-3">
              <p className="text-sm text-green-800">Settings saved successfully!</p>
            </div>
          </div>
        </div>
      )}
      {/* Settings Form */}
      <div className="space-y-8">
        {/* General Settings */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Settings className="w-5 h-5 mr-2" />
            General Settings
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                System Name
              </label>
              <input
                type="text"
                value={settings?.systemName}
                onChange={(e) => handleInputChange('systemName', e?.target?.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Default User Role
              </label>
              <select
                value={settings?.defaultUserRole}
                onChange={(e) => handleInputChange('defaultUserRole', e?.target?.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="org_viewer">Organization Viewer</option>
                <option value="org_user">Organization User</option>
                <option value="org_admin">Organization Admin</option>
              </select>
            </div>
            
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                System Description
              </label>
              <textarea
                value={settings?.systemDescription}
                onChange={(e) => handleInputChange('systemDescription', e?.target?.value)}
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>

        {/* User Management */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            User Management
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Max Organizations per User
              </label>
              <input
                type="number"
                min="1"
                max="10"
                value={settings?.maxOrganizationsPerUser}
                onChange={(e) => handleInputChange('maxOrganizationsPerUser', parseInt(e?.target?.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Session Timeout (minutes)
              </label>
              <input
                type="number"
                min="5"
                max="480"
                value={settings?.sessionTimeout}
                onChange={(e) => handleInputChange('sessionTimeout', parseInt(e?.target?.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            
            <div className="flex items-center">
              <input
                type="checkbox"
                id="allowNewRegistrations"
                checked={settings?.allowNewRegistrations}
                onChange={(e) => handleInputChange('allowNewRegistrations', e?.target?.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="allowNewRegistrations" className="ml-2 block text-sm text-gray-900">
                Allow new user registrations
              </label>
            </div>
            
            <div className="flex items-center">
              <input
                type="checkbox"
                id="maintenanceMode"
                checked={settings?.maintenanceMode}
                onChange={(e) => handleInputChange('maintenanceMode', e?.target?.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="maintenanceMode" className="ml-2 block text-sm text-gray-900">
                <span className="font-medium">Maintenance Mode</span>
                <span className="block text-xs text-gray-500">Disable system for maintenance</span>
              </label>
            </div>
          </div>
        </div>

        {/* Security Settings */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Security Settings
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Minimum Password Length
              </label>
              <input
                type="number"
                min="6"
                max="32"
                value={settings?.passwordMinLength}
                onChange={(e) => handleInputChange('passwordMinLength', parseInt(e?.target?.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Audit Log Retention (days)
              </label>
              <input
                type="number"
                min="30"
                max="365"
                value={settings?.auditLogRetention}
                onChange={(e) => handleInputChange('auditLogRetention', parseInt(e?.target?.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            
            <div className="flex items-center">
              <input
                type="checkbox"
                id="requirePasswordSpecialChars"
                checked={settings?.requirePasswordSpecialChars}
                onChange={(e) => handleInputChange('requirePasswordSpecialChars', e?.target?.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="requirePasswordSpecialChars" className="ml-2 block text-sm text-gray-900">
                Require special characters in passwords
              </label>
            </div>
            
            <div className="flex items-center">
              <input
                type="checkbox"
                id="emailNotifications"
                checked={settings?.emailNotifications}
                onChange={(e) => handleInputChange('emailNotifications', e?.target?.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="emailNotifications" className="ml-2 block text-sm text-gray-900">
                Enable email notifications
              </label>
            </div>
          </div>
        </div>

        {/* System Maintenance */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            System Maintenance
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Backup Frequency
              </label>
              <select
                value={settings?.backupFrequency}
                onChange={(e) => handleInputChange('backupFrequency', e?.target?.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="hourly">Hourly</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
          </div>
          
          <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
            <div className="flex">
              <AlertTriangle className="w-5 h-5 text-yellow-400" />
              <div className="ml-3">
                <h4 className="text-sm font-medium text-yellow-800">
                  Important Notes
                </h4>
                <div className="mt-2 text-sm text-yellow-700">
                  <ul className="list-disc pl-5 space-y-1">
                    <li>Changes to security settings may require users to re-authenticate</li>
                    <li>Maintenance mode will make the system unavailable to all users except super admins</li>
                    <li>Backup settings may require additional storage configuration</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SystemSettings;